import { useState, useEffect } from "react";
import axios from "axios";
import { Apiurl } from "../../../shared/services/apirest";
import { getAuthHeader, checkParasitePresence } from "../utils/investigadorUtils";
import LocationService from "../../../shared/services/LocationService";

export const useAnonymizedReport = () => {
    const [data, setData] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(true);
    const [municipiosList, setMunicipiosList] = useState([]);

    // Filter states
    const [filters, setFilters] = useState({
        startDate: "",
        endDate: "",
        municipio: "",
        riesgo: "",
        proyecto: "",
        parasito: "",
        resultado: "POSITIVO"
    });

    const calculateIntensity = (name, hpg) => {
        if (!hpg || hpg <= 0) return "Negativa";
        if (name === "Ascaris") {
            if (hpg >= 1 && hpg <= 4999) return "Leve";
            if (hpg >= 5000 && hpg <= 49999) return "Moderada";
            return "Alta";
        }
        if (name === "Trichuris") {
            if (hpg >= 1 && hpg <= 999) return "Leve";
            if (hpg >= 1000 && hpg <= 9999) return "Moderada";
            return "Alta";
        }
        if (name === "Uncinarias") {
            if (hpg >= 1 && hpg <= 1999) return "Leve";
            if (hpg >= 2000 && hpg <= 3999) return "Moderada";
            return "Alta";
        }
        return "Negativa";
    };

    const getClimateCondition = (dateStr) => {
        if (!dateStr) return "N/D";
        const month = new Date(dateStr).getMonth() + 1;
        if ([12, 1, 2, 3].includes(month)) return "Seco";
        if ([5, 6, 7, 8, 9].includes(month)) return "Lluvioso";
        return "Transición";
    };

    const getQuarter = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        const q = Math.floor((d.getMonth() + 3) / 3);
        return `${d.getFullYear()}-Q${q}`;
    };

    const deriveStatus = (c) => {
        if (!c.fecha_registro) return "Desconocido";
        const daysDiff = (new Date() - new Date(c.fecha_registro)) / (1000 * 60 * 60 * 24);
        return daysDiff < 30 ? 'Activo' : 'Cerrado';
    };

    const getHash = (str) => {
        if (!str) return "N/A";
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        return "P-" + Math.abs(hash).toString(16).substring(0, 8).toUpperCase();
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const [resCasos, resPacientes] = await Promise.all([
                axios.get(`${Apiurl}casos/`, getAuthHeader()),
                axios.get(`${Apiurl}casos/pacientes/`, getAuthHeader())
            ]);

            const pacientesMap = {};
            resPacientes.data.forEach(p => { pacientesMap[p.id] = p; });

            const enrichedCasos = resCasos.data.map(c => {
                const pInfo = pacientesMap[c.paciente] || {};
                const hpgMap = c.intensidad_hpg || {};
                const pDetectados = c.parasitos_detectados || [];

                return {
                    ...c,
                    ID_Caso: `CASE-${c.id}`,
                    ID_Paciente_Anonimizado: getHash(pInfo.documento || String(pInfo.id)),
                    Fecha_Registro: c.fecha_registro,
                    Edad: pInfo.edad || "N/A",
                    Sexo: pInfo.sexo || "N/A",
                    Municipio: pInfo.municipio || "Desconocido",
                    Zona: pInfo.zona || "Desconocida",
                    Latitud_Aproximada: c.latitud ? parseFloat(c.latitud).toFixed(2) : "N/D",
                    Longitud_Aproximada: c.longitud ? parseFloat(c.longitud).toFixed(2) : "N/D",
                    Ascaris_lumbricoides_Presente: pDetectados.includes("Ascaris") ? "Sí" : "No",
                    Trichuris_trichiura_Presente: pDetectados.includes("Trichuris") ? "Sí" : "No",
                    Uncinarias_Presentes: pDetectados.includes("Uncinarias") ? "Sí" : "No",
                    Conteo_HPG_Ascaris: hpgMap["Ascaris"] || 0,
                    Conteo_HPG_Trichuris: hpgMap["Trichuris"] || 0,
                    Conteo_HPG_Uncinarias: hpgMap["Uncinarias"] || 0,
                    Intensidad_Infección_Ascaris: calculateIntensity("Ascaris", hpgMap["Ascaris"]),
                    Intensidad_Infección_Trichuris: calculateIntensity("Trichuris", hpgMap["Trichuris"]),
                    Intensidad_Infección_Uncinarias: calculateIntensity("Uncinarias", hpgMap["Uncinarias"]),
                    Número_Parásitos_Detectados: pDetectados.length,
                    Nivel_Riesgo_Epidemiológico: c.nivel_peligrosidad || "BAJO",
                    Periodo_Epidemiológico: getQuarter(c.fecha_registro),
                    Condición_Climática_Asociada: getClimateCondition(c.fecha_registro),
                    Temperatura_Promedio: c.temperatura || "N/D",
                    Humedad_Promedio: c.humedad || "N/D",
                    Estado_Caso: deriveStatus(c),
                    Codigo_Proyecto_Investigacion: c.codigo_proyecto_investigacion ||
                        c.paciente_codigo_proyecto_investigacion ||
                        pInfo.codigo_proyecto_investigacion ||
                        pInfo.proyecto ||
                        "N/A"
                };
            });

            setData(enrichedCasos);
            setFiltered(enrichedCasos);
        } catch (err) {
            console.error("Error loading data:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        LocationService.getHierarchy().then(data => {
            setMunicipiosList(Object.keys(data).sort());
        });
    }, []);

    useEffect(() => {
        let res = data;
        const { startDate, endDate, municipio, riesgo, proyecto, parasito, resultado } = filters;
        if (startDate) res = res.filter(c => c.Fecha_Registro >= startDate);
        if (endDate) res = res.filter(c => c.Fecha_Registro <= endDate);
        if (municipio) res = res.filter(c => c.Municipio === municipio);
        if (riesgo) res = res.filter(c => c.Nivel_Riesgo_Epidemiológico === riesgo);
        if (proyecto) {
            const query = proyecto.toLowerCase();
            res = res.filter(c => c.Codigo_Proyecto_Investigacion?.toLowerCase().includes(query));
        }
        if (parasito) {
            res = res.filter(c => {
                const isPresent = checkParasitePresence(c.parasitos_detectados, parasito);
                return resultado === "POSITIVO" ? isPresent : !isPresent;
            });
        }
        setFiltered(res);
    }, [data, filters]);

    return { filtered, loading, municipiosList, filters, setFilters };
};
